import { Button } from '@ctw/shared/components/button';
import { useHotloadStatus } from '@ctw/shared/hooks/use-hotload-status';
import { useUiFlag } from '@ctw/shared/hooks/use-ui-preference';
import { getResourceTypeDisplayName } from '@ctw/shared/utils/fhir/resource-type-display-names';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import pluralize from 'pluralize';
import { useCallback, useId, useMemo, useRef } from 'react';

export interface PatientHotloadSkippedResourcesBannerProps {
  testId?: string;
  upid?: string;
  /** Whether the banner can be dismissed. Defaults to `true`. */
  dismissable?: boolean;
}

/** Returns a upid-scoped UI flag key so dismissing the banner for one patient doesn't hide it for another. */
const getUiFlagHotloadSkippedResourcesBannerHidden = (upid: string | undefined) =>
  `patient-hotload-skipped-resources-banner-hidden-${upid}`;

/**
 * Renders each `[resourceType, count]` pair as a natural-language, pluralized noun phrase joined
 * with an Oxford comma (e.g. `["2 documents", "1 diagnostic report"]` -> "2 documents and 1
 * diagnostic report").
 */
const formatSkippedResourceSummary = (skippedEntries: Array<[string, number]>): string => {
  const phrases = skippedEntries.map(
    ([resourceType, count]) =>
      `${count} ${pluralize(getResourceTypeDisplayName(resourceType), count)}`,
  );

  return new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(phrases);
};

/**
 * Warns that some of a patient's records skipped hotstorage loading and can't currently be
 * displayed, with a per-type count. Renders nothing when `useHotloadStatus`'s
 * `skippedResourceCountByType` has no entry greater than 0 for `upid` — whether because nothing
 * was skipped, or because FQS hasn't reported a skipped count for a partially loaded type yet (the
 * shape a UPID keeps until it next reloads if it was partially loaded before FQS started
 * persisting this count). The banner never reads the rollup `state` itself.
 *
 * Dismissal is scoped to the current session and to this patient, via `useUiFlag`'s
 * `sessionStorage` area — dismissing while viewing one patient must not hide the banner for
 * another, and the warning returns in a new session since a later hotload can change the counts.
 */
export const PatientHotloadSkippedResourcesBanner = ({
  testId,
  upid,
  dismissable = true,
}: PatientHotloadSkippedResourcesBannerProps) => {
  const { skippedResourceCountByType } = useHotloadStatus(upid, { poll: false });
  const flagKey = useMemo(() => getUiFlagHotloadSkippedResourcesBannerHidden(upid), [upid]);
  const [flagHidden, setFlagHidden] = useUiFlag(flagKey, false, 'sessionStorage');
  const hidden = dismissable && flagHidden;
  const titleId = useId();
  const dismissButtonRef = useRef<HTMLButtonElement>(null);

  const handleDismiss = useCallback(() => {
    dismissButtonRef.current?.blur();
    setFlagHidden(true);
  }, [setFlagHidden]);

  const skippedEntries = Object.entries(skippedResourceCountByType).filter(
    ([, count]) => count > 0,
  );

  if (skippedEntries.length === 0) {
    return null;
  }

  const isSingularSubject = skippedEntries.length === 1 && skippedEntries[0][1] === 1;

  const body = (
    <>
      {formatSkippedResourceSummary(skippedEntries)} {isSingularSubject ? 'is' : 'are'} in Zus but
      can't be displayed right now. Zus is aware and working on a fix.
    </>
  );

  const content = (
    <div
      data-testid={testId}
      role="region"
      aria-labelledby={titleId}
      className={tw`mb-2 flex items-start justify-between gap-4 rounded-lg border border-caution-main/15 bg-caution-background p-4 text-caution-text`}
    >
      <div>
        <div className={tw`flex items-center gap-3`}>
          <FontAwesomeIcon icon={faTriangleExclamation} className={tw`relative top-3 h-5 w-5`} />
          <h2 id={titleId} className={tw`font-bold`}>
            Some records aren't showing
          </h2>
        </div>
        <div className={tw`mt-0.5 pl-8`}>{body}</div>
      </div>
      {dismissable && (
        <Button
          ref={dismissButtonRef}
          type="button"
          variant="circular"
          size="xs"
          ariaLabel="Dismiss"
          className={tw`flex items-center justify-center bg-transparent p-1 text-caution-text shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15`}
          onClick={handleDismiss}
        >
          <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
        </Button>
      )}
    </div>
  );

  if (!dismissable) {
    return content;
  }

  return (
    <div
      inert={hidden}
      className={twx('grid transition-[grid-template-rows,opacity] duration-300 ease-out', {
        'grid-rows-[0fr] opacity-0': hidden,
        'grid-rows-[1fr] opacity-100': !hidden,
      })}
    >
      <div className={tw`min-h-0 overflow-hidden`}>{content}</div>
    </div>
  );
};
